/**
 * Custom / template / AI personalization on the PDP.
 *
 * Live crop/composite happens on a <canvas>. When the configurator slide
 * (`[data-fc-canvas] [data-fc-canvas-stage]`) exists, the bitmap is pushed
 * into `[data-fc-canvas-art]` and `custom-artwork:changed` is dispatched.
 * When it does not (plain upload products with no design_artwork metafield),
 * the editor mounts its own size-locked workspace
 * (`[data-custom-workspace] [data-custom-stage] canvas[data-custom-art]`).
 * Print PDF is a dash compose (immediate after upload, debounced on crop).
 */
if (!customElements.get('custom-product-editor')) {
  const SIZE_RE = /(\d+(?:[.,]\d+)?)\s*[x×X]\s*(\d+(?:[.,]\d+)?)/;
  const ASPECT_RE = /^\s*(\d+(?:\.\d+)?)\s*[:\/x×]\s*(\d+(?:\.\d+)?)\s*$/;
  const MAX_ZOOM = 3;
  const MIN_ZOOM = 1;
  const CANVAS_LONG = 1000;
  const FONT_REFERENCE_WIDTH = 1000;
  const COMPOSE_MS = 450;
  const LOW_RES_DPI = 150;

  class CustomProductEditor extends HTMLElement {
    connectedCallback() {
      this.dash = (this.dataset.dashUrl || '').replace(/\/$/, '');
      this.handle = this.dataset.handle || '';
      this.fileInput = this.querySelector('[data-custom-file]');
      this.pick = this.querySelector('[data-custom-pick]');
      this.aiBtn = this.querySelector('[data-custom-ai]');
      this.resetBtn = this.querySelector('[data-custom-reset]');
      this.status = this.querySelector('[data-custom-status]');
      this.fieldsEl = this.querySelector('[data-custom-fields]');
      this.zoom = this.querySelector('[data-custom-zoom]');
      this.zoomWrap = this.querySelector('[data-custom-zoom-wrap]');
      this.approveBtn = this.querySelector('[data-custom-approve]');
      this.editBtn = this.querySelector('[data-custom-edit]');
      this.hint = this.querySelector('[data-custom-hint]');
      this.ownWorkspace = this.querySelector('[data-custom-workspace]');
      this.ownStage = this.querySelector('[data-custom-stage]');

      this.template = null;
      this.imagePath = null;
      this.token = null;
      this.crop = { scale: 1, x: 0, y: 0 };
      this.sizeLabel = '';
      this.mainImage = null;
      this.baseImage = null;
      this.fieldImages = {};
      this.fieldPaths = {};
      this.fieldCrops = {};
      this.objectUrls = [];
      this.artBlobUrl = null;
      this.paintGen = 0;
      this.composeSeq = 0;
      this.composeTimer = null;
      this.raf = 0;
      this.dragging = false;
      this.lastPointer = null;
      this.resetting = false;
      this.originalArt = null;
      this.originalGallery = null;
      this.galleryImg = null;
      this.galleryObserver = null;
      this.galleryApplying = false;
      this.galleryPreviewUrl = null;
      this.boundStage = null;
      this.composeFailed = false;
      this.approved = false;

      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d', { alpha: false });

      this.boundPointerDown = this.onPointerDown.bind(this);
      this.boundPointerMove = this.onPointerMove.bind(this);
      this.boundPointerUp = this.onPointerUp.bind(this);
      this.boundWheel = this.onWheel.bind(this);
      this.boundDocChange = this.onDocumentChange.bind(this);
      this.boundCartUpdated = this.onCartUpdated.bind(this);
      this.boundAjaxAdded = () => this.resetAfterAdd();
      this.boundCartPub = (event) => {
        if (event?.source === 'product-form') this.resetAfterAdd();
      };
      this.boundSizePub = () => this.onSizeMaybeChanged();
      this.boundFrameConfig = (event) => {
        try {
          const cfg = event?.target;
          this.matChoice = cfg?.selectedMatEntry?.name || '';
          this.frameChoice = cfg?.selectedColorEntry?.name || cfg?.selectedColorEntry?.label || event?.detail?.frameColor || '';
          this.writeConfigProperties();
          this.ensureWorkspace();
          this.schedulePaint('settle');
        } catch (e) {
          /* never break the PDP */
        }
      };
      this.boundAtcGuard = this.onAtcGuard.bind(this);

      this.pick?.addEventListener('click', () => this.requestFile());
      // Yaris: baskı yönü (dikey / yatay) ve döndürme
      this.orientation = 'portrait';
      this.rotation = 0;
      this.rotPaths = {};
      this.orientBtns = [...this.querySelectorAll('[data-custom-orient]')];
      this.orientBtns.forEach((btn) => btn.addEventListener('click', () => this.setOrientation(btn.dataset.customOrient)));
      this.rotateBtn = this.querySelector('[data-custom-rotate]');
      this.rotateBtn?.addEventListener('click', () => this.rotateImage());
      // Yaris: kesikli çizgili yükleme kutusu — tıklayınca dosya seçtirir, sürükle-bırak da kabul eder.
      this.drop = this.querySelector('[data-custom-drop]');
      if (this.drop) {
        this.drop.addEventListener('click', () => this.requestFile());
        ['dragenter', 'dragover'].forEach((type) => {
          this.drop.addEventListener(type, (event) => {
            event.preventDefault();
            this.drop.classList.add('is-over');
          });
        });
        ['dragleave', 'dragend', 'drop'].forEach((type) => {
          this.drop.addEventListener(type, () => this.drop.classList.remove('is-over'));
        });
        this.drop.addEventListener('drop', (event) => {
          event.preventDefault();
          const file = event.dataTransfer?.files?.[0];
          if (!file || !this.fileInput) return;
          if (!this.hasSize()) {
            this.promptSize();
            return;
          }
          try {
            const transfer = new DataTransfer();
            transfer.items.add(file);
            this.fileInput.files = transfer.files;
          } catch (e) {
            return;
          }
          this.onMainFile();
        });
      }
      this.fileInput?.addEventListener('change', () => this.onMainFile());
      this.aiBtn?.addEventListener('click', () => this.runAi());
      this.resetBtn?.addEventListener('click', () => this.reset({ restoreArt: true }));
      this.zoom?.addEventListener('input', () => {
        this.crop.scale = this.clampZoom(Number(this.zoom.value) || 1);
        this.constrainCrop();
        this.schedulePaint('live');
      });
      this.zoom?.addEventListener('change', () => {
        this.schedulePaint('settle');
        this.kickCompose();
      });
      this.approveBtn?.addEventListener('click', () => this.onApprove());
      this.editBtn?.addEventListener('click', () => this.onEditCrop());

      document.addEventListener('change', this.boundDocChange, true);
      document.addEventListener('cart:updated', this.boundCartUpdated);
      document.addEventListener('ajaxProduct:added', this.boundAjaxAdded);
      document.addEventListener('frame-config:change', this.boundFrameConfig);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this.boundCartPub);
        this.unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.boundSizePub);
        this.unsubOption = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.boundSizePub);
      }

      this.snapshotOriginalArt();
      this.activating = this.dataset.plain === 'true';
      if (this.activating) this.bindAtcGuard();
      this.loadTemplate()
        .catch(() => {
          /* never break the rest of the PDP */
        })
        .finally(() => {
          this.activating = false;
        });
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.boundDocChange, true);
      document.removeEventListener('cart:updated', this.boundCartUpdated);
      document.removeEventListener('ajaxProduct:added', this.boundAjaxAdded);
      document.removeEventListener('frame-config:change', this.boundFrameConfig);
      this.unsubCart?.();
      this.unsubVariant?.();
      this.unsubOption?.();
      this.unbindStage();
      this.unbindAtcGuard();
      this.disconnectGalleryObserver();
      this.revokeAll();
    }

    /* ---------------- bootstrap ---------------- */

    async loadTemplate() {
      if (this.handle && this.dash) {
        try {
          const res = await fetch(`${this.dash}/api/custom/templates/by-handle/${encodeURIComponent(this.handle)}`);
          if (res.ok) {
            this.template = await res.json();
            await this.activate();
            return;
          }
        } catch (e) {
          /* dash unreachable — fall through to plain upload if tagged */
        }
      }
      if (this.dataset.plain === 'true') {
        this.template = null;
        await this.activate();
      }
    }

    async activate() {
      this.hidden = false;
      this.sizeLabel = this.readSizeLabel();
      this.resizeCanvas();
      this.ensureWorkspace();
      this.applyWorkspaceRatio();
      this.renderFields();
      this.syncModeUi();
      this.syncSizeGate();
      this.syncOrientUi();
      this.bindStage();
      this.bindAtcGuard();
      this.schedulePaint('settle');
      if (this.template?.artwork_url) {
        try {
          this.baseImage = await this.loadImage(this.template.artwork_url, true);
          if (this.mode() !== 'upload') this.schedulePaint('settle');
        } catch (e) {
          /* preview artwork is optional */
        }
      }
    }

    isPlain() {
      return this.dataset.plain === 'true' && !this.template;
    }

    needsPrintApproval() {
      return this.isPlain() || this.mode() === 'upload' || this.mode() === 'ai';
    }

    isCropping() {
      return this.needsPrintApproval() && Boolean(this.mainImage) && !this.approved;
    }

    syncCropChrome() {
      const cropping = this.isCropping();
      this.classList.toggle('is-cropping', cropping);
      if (cropping || !this.mainImage) this.clearArArt();
      if (this.approveBtn) this.approveBtn.hidden = !cropping;
      if (this.editBtn) this.editBtn.hidden = cropping || !this.approved;
      this.ensureWorkspace();
      this.applyWorkspaceRatio();
      this.bindStage();
      this.syncZoomUi();
    }

    onApprove() {
      if (!this.mainImage || !this.imagePath) {
        this.setStatus(this.dataset.needUploadLabel || '', 'warn');
        return;
      }
      this.approved = true;
      this.syncCropChrome();
      this.resizeCanvas();
      this.schedulePaint('settle');
      this.kickCompose(true);
      if (!this.warnIfLowRes()) this.setStatus(this.dataset.approvedLabel || '');
    }

    onEditCrop() {
      this.approved = false;
      this.token = null;
      this.clearLineProperties();
      this.syncCropChrome();
      this.resizeCanvas();
      this.schedulePaint('settle');
    }

    mode() {
      return this.template?.mode || 'upload';
    }

    canCrop() {
      return this.isCropping();
    }

    syncModeUi() {
      const mode = this.mode();
      const plain = this.isPlain();
      this.classList.toggle('custom-editor--plain', plain);
      this.classList.toggle('custom-editor--upload', mode === 'upload');
      this.classList.toggle('custom-editor--template', mode === 'template');
      this.classList.toggle('custom-editor--ai', mode === 'ai');
      if (this.pick) this.pick.hidden = mode === 'template';
      if (this.aiBtn) this.aiBtn.hidden = plain || mode !== 'ai';
      if (this.fieldsEl) this.fieldsEl.hidden = plain;
      if (this.hint) this.hint.hidden = mode === 'template';
      this.syncZoomUi();
    }

    syncZoomUi() {
      if (this.zoomWrap) this.zoomWrap.hidden = !this.canCrop();
      // Yaris: dosya yüklendiyse kutuyu gizle, "Başka dosya yükle" ikincil butona dönsün, "Sıfırla" görünsün.
      const hasFile = Boolean(this.mainImage);
      this.classList.toggle('has-file', hasFile);
      if (this.pick) this.pick.classList.toggle('button--secondary', hasFile);
      if (this.zoom) this.zoom.value = String(this.crop.scale);
      const stage = this.getStage();
      if (stage) stage.classList.toggle('is-custom-editing', this.canCrop());
    }

    renderFields() {
      if (!this.fieldsEl) return;
      this.fieldsEl.innerHTML = '';
      const fields = Array.isArray(this.template?.fields) ? this.template.fields : [];
      const photoLimit = Math.max(1, Number(this.template?.photo_count) || fields.filter((f) => f.type === 'photo').length || 1);
      let photos = 0;
      for (const field of fields) {
        if (!field?.key) continue;
        if (field.type === 'photo') {
          photos += 1;
          if (photos > photoLimit) continue;
        }
        const wrap = document.createElement('label');
        wrap.className = 'custom-editor__field';
        const caption = document.createElement('span');
        caption.textContent = field.label || field.key;
        wrap.appendChild(caption);

        if (field.type === 'text') {
          const input = document.createElement('input');
          input.type = 'text';
          input.dataset.customField = field.key;
          input.addEventListener('input', () => {
            this.schedulePaint('settle');
            this.kickCompose();
          });
          wrap.appendChild(input);
        } else if (field.type === 'photo') {
          const row = document.createElement('div');
          row.className = 'custom-editor__field-file';
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.dataset.customPhoto = field.key;
          input.addEventListener('change', () => this.onFieldPhoto(field.key, input));
          row.appendChild(input);
          wrap.appendChild(row);
        }
        this.fieldsEl.appendChild(wrap);
      }
    }

    /* ---------------- files / AI ---------------- */

    async onMainFile() {
      const file = this.fileInput?.files?.[0];
      if (!file) return;
      const maxBytes = Number(this.dataset.maxBytes) || 50 * 1024 * 1024;
      if (file.size > maxBytes) {
        this.setStatus(this.dataset.tooLargeLabel || '', true);
        this.fileInput.value = '';
        return;
      }
      try {
        const localUrl = this.rememberUrl(URL.createObjectURL(file));
        this.mainImage = await this.loadImage(localUrl, false);
        this.mainFile = file;
        this.rotation = 0;
        this.rotPaths = {};
        this.autoOrientation(this.mainImage);
        this.crop = { scale: 1, x: 0, y: 0 };
        this.approved = false;
        this.token = null;
        this.clearLineProperties();
        this.syncCropChrome();
        this.resizeCanvas();
        this.syncZoomUi();
        this.schedulePaint('settle');
        this.setStatus(this.dataset.uploadingLabel || '…');
        const json = await this.uploadFile(file);
        this.rotPaths[0] = json.path;
        if (!this.rotation) this.imagePath = json.path;
        if (this.mode() === 'ai' && !this.isPlain()) {
          await this.runAi();
          return;
        }
        try {
          const low = this.warnIfLowRes();
          this.kickCompose(true);
          if (!low && this.needsPrintApproval() && !this.approved) this.setStatus('');
        } catch (err) {
          /* warning must never block compose */
        }
      } catch (e) {
        this.setStatus(this.dataset.errorLabel || '', true);
        this.composeFailed = true;
      }
    }

    async onFieldPhoto(key, input) {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const localUrl = this.rememberUrl(URL.createObjectURL(file));
        this.fieldImages[key] = await this.loadImage(localUrl, false);
        this.attachFieldThumb(input, localUrl);
        this.schedulePaint('settle');
        this.setStatus(this.dataset.uploadingLabel || '…');
        const json = await this.uploadFile(file);
        this.fieldPaths[key] = json.path;
        input.dataset.path = json.path;
        this.setStatus('');
        this.kickCompose(true);
      } catch (e) {
        this.setStatus(this.dataset.errorLabel || '', true);
        this.composeFailed = true;
      }
    }

    attachFieldThumb(input, url) {
      const row = input.closest('.custom-editor__field-file');
      if (!row) return;
      let thumb = row.querySelector('.custom-editor__thumb');
      if (!thumb) {
        thumb = document.createElement('img');
        thumb.className = 'custom-editor__thumb';
        thumb.alt = '';
        row.appendChild(thumb);
      }
      thumb.src = url;
    }

    /*
     * BUYUK BEYOGLU paneli (2026-10-28): izin mağazanın app proxy'sinden (yarisposter.com/apps/bb/ozel),
     * dosya panele 8 MB'lık parçalarla doğrudan gider (en çok 3 parça aynı anda; kopan parça 2 kez daha denenir).
     */
    async uploadFile(file) {
      const permit = await fetch(`${this.dash}/yukleme-izni`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ad: file.name, boyut: file.size, tur: file.type }),
      });
      const p = await permit.json().catch(() => ({}));
      if (!permit.ok || !p.bilet || !p.adres) throw new Error(p.message || p.mesaj || 'upload');
      const size = Number(p.parca) || 8 * 1024 * 1024;
      const parts = Math.ceil(file.size / size);
      const send = async (i) => {
        for (let attempt = 0; ; attempt++) {
          const q = new URLSearchParams({ bilet: p.bilet, parca: String(i), parcalar: String(parts) });
          const res = await fetch(`${p.adres}?${q}`, { method: 'POST', body: file.slice(i * size, (i + 1) * size) }).catch(() => null);
          if (res && res.ok) return;
          if ((res && res.status < 500) || attempt >= 2) {
            const j = res ? await res.json().catch(() => ({})) : {};
            throw new Error(j.message || 'upload');
          }
        }
      };
      let next = 0;
      await Promise.all(
        Array.from({ length: Math.min(3, parts) }, async () => {
          while (next < parts) await send(next++);
        }),
      );
      const done = await fetch(`${p.adres}/bitir?${new URLSearchParams({ bilet: p.bilet })}`, { method: 'POST' });
      const json = await done.json().catch(() => ({}));
      if (!done.ok || !json.path) throw new Error(json.message || 'upload');
      return json;
    }

    async runAi() {
      if (!this.imagePath || !this.template?.id) return;
      this.setBusy(true);
      this.setStatus(this.dataset.generatingLabel || this.dataset.aiLabel || '…');
      try {
        const res = await fetch(`${this.dash}/api/custom/ai`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ template_id: this.template.id, image_path: this.imagePath }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.setStatus(json.message || this.dataset.errorLabel || '', true);
          return;
        }
        this.imagePath = json.path;
        this.mainImage = await this.loadImage(json.url, true);
        this.crop = { scale: 1, x: 0, y: 0 };
        this.syncZoomUi();
        this.schedulePaint('settle');
        this.setStatus('');
        this.kickCompose(true);
      } catch (e) {
        this.setStatus(this.dataset.errorLabel || '', true);
        this.composeFailed = true;
      } finally {
        this.setBusy(false);
      }
    }

    /* ---------------- size / canvas ---------------- */

    /* Yaris: baskı yönü — yatayda en ve boy yer değiştirir (baskı sunucusuna da "70x50" gibi gider). */
    parseSize(label) {
      const p = this.parseSizeRaw(label);
      const landscape = this.orientation === 'landscape';
      if ((landscape && p.h > p.w) || (!landscape && p.w > p.h)) return { w: p.h, h: p.w, label: p.label };
      return p;
    }

    parseSizeRaw(label) {
      const raw = String(label || '');
      const parsed = window.YarisFramePreview?.parseSize?.(raw);
      if (parsed?.w && parsed?.h) {
        const w = parsed.w / 10;
        const h = parsed.h / 10;
        if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
          return { w, h, label: raw };
        }
      }
      const m = SIZE_RE.exec(raw);
      if (!m) return { w: 50, h: 70, label: raw || '50x70' };
      return {
        w: parseFloat(m[1].replace(',', '.')),
        h: parseFloat(m[2].replace(',', '.')),
        label: raw,
      };
    }

    /* ---------------- Yaris: önce boyut, yön, döndürme, duvarında gör ---------------- */

    hasSize() {
      const root = this.closest('product-info') || document;
      const sheet = root.querySelector('size-option-sheet');
      if (sheet) {
        const select = sheet.querySelector('select');
        if (!select) return true;
        const selected = select.options?.[select.selectedIndex];
        return Boolean(select.value) && selected?.dataset?.placeholder !== 'true';
      }
      const selects = root.querySelectorAll('variant-selects select, select[name^="options["]');
      for (const select of selects) {
        if (!select.value) return false;
      }
      return true;
    }

    promptSize() {
      this.setStatus(this.dataset.needSizeLabel || '', 'warn');
      const root = this.closest('product-info') || document;
      const sheet = root.querySelector('size-option-sheet');
      const target = sheet || root.querySelector('variant-selects, .product-form__input');
      try {
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        /* scrolling is cosmetic */
      }
      const trigger = sheet?.querySelector('.size-option-sheet__trigger');
      if (trigger) {
        setTimeout(() => {
          try {
            trigger.click();
          } catch (e) {
            /* opening the size sheet is optional */
          }
        }, 400);
      }
    }

    syncSizeGate() {
      const ok = this.hasSize();
      this.classList.toggle('needs-size', !ok);
      const msg = this.dataset.needSizeLabel || '';
      if (ok && msg && this.status && !this.status.hidden && this.status.textContent === msg) this.setStatus('');
    }

    requestFile() {
      if (!this.hasSize()) {
        this.promptSize();
        return;
      }
      this.fileInput?.click();
    }

    autoOrientation(img) {
      const w = img?.naturalWidth || 0;
      const h = img?.naturalHeight || 0;
      if (!(w > 0) || !(h > 0)) return;
      const ratio = w / h;
      if (ratio > 1.05) this.orientation = 'landscape';
      else if (ratio < 0.95) this.orientation = 'portrait';
      this.syncOrientUi();
    }

    setOrientation(next) {
      const value = next === 'landscape' ? 'landscape' : 'portrait';
      if (value === this.orientation) return;
      this.orientation = value;
      this.crop = { scale: 1, x: 0, y: 0 };
      this.approved = false;
      this.token = null;
      this.clearLineProperties();
      if (this.zoom) this.zoom.value = '1';
      this.syncOrientUi();
      this.syncCropChrome();
      this.resizeCanvas();
      this.applyWorkspaceRatio();
      this.schedulePaint('settle');
      if (this.mainImage && !this.warnIfLowRes()) this.setStatus('');
    }

    syncOrientUi() {
      (this.orientBtns || []).forEach((btn) => {
        const on = btn.dataset.customOrient === this.orientation;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    async rotateImage() {
      if (!this.mainFile || !this.mainImage || this.rotating) return;
      this.rotating = true;
      this.setBusy(true);
      this.setStatus(this.dataset.uploadingLabel || '…');
      try {
        const next = ((this.rotation || 0) + 90) % 360;
        const source = await this.loadImage(this.rememberUrl(URL.createObjectURL(this.mainFile)), false);
        const file = next === 0 ? this.mainFile : await this.rotatedFile(source, next);
        const img = next === 0 ? source : await this.loadImage(this.rememberUrl(URL.createObjectURL(file)), false);
        this.rotation = next;
        this.mainImage = img;
        this.autoOrientation(img);
        this.crop = { scale: 1, x: 0, y: 0 };
        this.approved = false;
        this.token = null;
        this.clearLineProperties();
        if (this.zoom) this.zoom.value = '1';
        this.syncCropChrome();
        this.resizeCanvas();
        this.applyWorkspaceRatio();
        this.schedulePaint('settle');
        if (this.rotPaths[next]) {
          this.imagePath = this.rotPaths[next];
        } else {
          this.imagePath = null;
          this.setStatus(this.dataset.uploadingLabel || '…');
          const json = await this.uploadFile(file);
          this.rotPaths[next] = json.path;
          if (this.rotation === next) this.imagePath = json.path;
        }
        if (!this.warnIfLowRes()) this.setStatus('');
      } catch (e) {
        this.setStatus(this.dataset.errorLabel || '', true);
      } finally {
        this.rotating = false;
        this.setBusy(false);
      }
    }

    rotatedFile(img, deg) {
      return new Promise((resolve, reject) => {
        const iw = img.naturalWidth;
        const ih = img.naturalHeight;
        const swap = deg === 90 || deg === 270;
        // Telefon tarayıcılarının tuval sınırı (~16 MP) aşılmasın
        const k = Math.min(1, Math.sqrt(16000000 / Math.max(1, iw * ih)));
        const w = Math.max(1, Math.round(iw * k));
        const h = Math.max(1, Math.round(ih * k));
        const canvas = document.createElement('canvas');
        canvas.width = swap ? h : w;
        canvas.height = swap ? w : h;
        const ctx = canvas.getContext('2d');
        const png = this.mainFile?.type === 'image/png';
        if (!png) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((deg * Math.PI) / 180);
        ctx.drawImage(img, -w / 2, -h / 2, w, h);
        const base = (this.mainFile?.name || 'tasarim').replace(/\.[^.]+$/, '');
        const type = png ? 'image/png' : 'image/jpeg';
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('rotate'));
              return;
            }
            resolve(new File([blob], `${base}-${deg}.${png ? 'png' : 'jpg'}`, { type }));
          },
          type,
          0.95
        );
      });
    }

    /* Onaylanan tasarım, ürün fotoğrafındaki "Duvarında gör" için yayınlanır (yaris-cerceve-studyo dinler). */
    publishArArt() {
      if (!this.needsPrintApproval() || !this.approved || !this.mainImage || this.isCropping() || !this.canvas.width) return;
      const { w, h } = this.parseSize(this.sizeLabel || this.readSizeLabel());
      this.arSeq = (this.arSeq || 0) + 1;
      const seq = this.arSeq;
      const send = (blob) => {
        if (seq !== this.arSeq || !blob) return;
        const url = URL.createObjectURL(blob);
        const prev = this.arUrl;
        this.arUrl = url;
        document.dispatchEvent(new CustomEvent('yaris:custom-art', { detail: { url, w, h } }));
        if (prev) setTimeout(() => URL.revokeObjectURL(prev), 4000);
      };
      try {
        this.canvas.toBlob(send, 'image/jpeg', 0.9);
      } catch (e) {
        /* tainted canvas — no wall preview */
      }
    }

    clearArArt() {
      this.arSeq = (this.arSeq || 0) + 1;
      if (!this.arUrl) return;
      const prev = this.arUrl;
      this.arUrl = null;
      document.dispatchEvent(new CustomEvent('yaris:custom-art', { detail: { url: null } }));
      setTimeout(() => URL.revokeObjectURL(prev), 4000);
    }

    readSizeLabel() {
      const root = this.closest('product-info') || document;
      const sheet = root.querySelector('size-option-sheet');
      if (sheet) {
        const select = sheet.querySelector('select');
        const selected = select?.options?.[select.selectedIndex];
        if (select?.value && selected?.dataset?.placeholder !== 'true') return select.value;
        const trig = sheet.querySelector('[data-size-value]')?.getAttribute('data-size-value');
        if (trig && SIZE_RE.test(trig)) return trig;
      }
      const chip = root.querySelector('[data-size-value].is-selected, [data-size-value][aria-checked="true"]');
      if (chip?.getAttribute('data-size-value')) return chip.getAttribute('data-size-value');
      const optionSelects = root.querySelectorAll('select[name^="options["], variant-selects select, product-info select');
      for (const select of optionSelects) {
        if (select?.value && SIZE_RE.test(select.value)) return select.value;
      }
      return (this.template?.print_sizes || [])[0] || '50x70';
    }

    applyWorkspaceRatio() {
      if (this.isCropping()) {
        const ratio = '4 / 3';
        if (this.ownWorkspace) this.ownWorkspace.style.setProperty('--custom-workspace-ratio', ratio);
        if (this.ownStage) this.ownStage.style.setProperty('--custom-workspace-ratio', ratio);
        return;
      }
      const { w, h } = this.parseSize(this.sizeLabel || this.readSizeLabel());
      if (!(w > 0) || !(h > 0)) return;
      const ratio = `${w} / ${h}`;
      if (this.ownWorkspace) this.ownWorkspace.style.setProperty('--custom-workspace-ratio', ratio);
      if (this.ownStage) {
        this.ownStage.style.setProperty('--custom-workspace-ratio', ratio);
        this.ownStage.style.setProperty('--fc-canvas-ratio', String(w / h));
      }
      const art = this.getConfiguratorArt();
      const stage = this.getConfiguratorStage();
      const media = stage?.closest('.fc-canvas');
      if (art) {
        art.dataset.artWidth = String(w);
        art.dataset.artHeight = String(h);
      }
      if (media) media.style.setProperty('--fc-canvas-ratio', ratio);
      if (stage) stage.style.setProperty('--fc-canvas-ratio', String(w / h));
    }

    warnIfLowRes() {
      const dpi = this.effectiveDpi();
      if (dpi == null) return false;
      if (dpi < LOW_RES_DPI) {
        this.setStatus(this.dataset.lowResLabel || '', 'warn');
        return true;
      }
      return false;
    }

    effectiveDpi() {
      if (!this.mainImage) return null;
      const { w, h } = this.parseSize(this.sizeLabel || this.readSizeLabel());
      if (!(w > 0) || !(h > 0)) return null;
      const iw = this.mainImage.naturalWidth;
      const ih = this.mainImage.naturalHeight;
      if (!(iw > 0) || !(ih > 0)) return null;
      const dest = this.cropDestination();
      if (!(dest.w > 0) || !(dest.h > 0)) return null;
      const scale = this.clampZoom(this.crop?.scale || 1);
      const cover = Math.max(dest.w / iw, dest.h / ih) * scale;
      const srcW = dest.w / cover;
      const srcH = dest.h / cover;
      return Math.min(srcW / (w / 2.54), srcH / (h / 2.54));
    }

    onDocumentChange(e) {
      const t = e.target;
      if (!t?.closest) return;
      if (t.closest('variant-selects, size-option-sheet') || t.matches?.('select[name^="options["]')) {
        this.onSizeMaybeChanged();
      }
    }

    onSizeMaybeChanged() {
      this.syncSizeGate();
      const next = this.readSizeLabel();
      if (!next) return;
      const a = this.parseSize(next);
      const b = this.parseSize(this.sizeLabel);
      if (this.sizeLabel && a.w === b.w && a.h === b.h) {
        this.sizeLabel = next;
        this.reapplyGalleryPreview();
        return;
      }
      const previous = this.sizeLabel;
      this.sizeLabel = next;
      const aspectChanged =
        !previous || !(a.h > 0) || !(b.h > 0) || Math.abs(a.w / a.h - b.w / b.h) > 0.015;
      if (aspectChanged) {
        this.crop = { scale: 1, x: 0, y: 0 };
        this.approved = false;
        this.token = null;
        this.clearLineProperties();
        if (this.zoom) this.zoom.value = '1';
        this.syncCropChrome();
      } else if (this.approved) {
        this.token = null;
        this.clearLineProperties();
      }
      this.resizeCanvas();
      this.ensureWorkspace();
      this.applyWorkspaceRatio();
      this.schedulePaint('settle');
      if (this.mainImage) this.warnIfLowRes();
      this.kickCompose(Boolean(this.approved));
    }

    resizeCanvas() {
      if (this.isCropping()) {
        this.canvas.width = 1200;
        this.canvas.height = 900;
        return;
      }
      const { w, h } = this.parseSize(this.sizeLabel);
      if (!(w > 0) || !(h > 0)) return;
      const scale = CANVAS_LONG / Math.max(w, h);
      this.canvas.width = Math.max(1, Math.round(w * scale));
      this.canvas.height = Math.max(1, Math.round(h * scale));
    }

    /* ---------------- local composite ---------------- */

    schedulePaint(kind) {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.paint(kind);
      });
    }

    paint(kind) {
      try {
        if (!this.ctx || !this.canvas.width) this.resizeCanvas();
        if (!this.ctx) return;
        const w = this.canvas.width;
        const h = this.canvas.height;
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(0, 0, w, h);

        if (this.isCropping() && this.mainImage) {
          this.paintCropScene(w, h);
          return;
        }

        const mode = this.mode();
        if (mode === 'template') {
          const base = this.baseImage || this.mainImage;
          if (base) this.drawFitted(base, 0, 0, w, h, 'cover');
        } else if (this.mainImage) {
          this.drawCover(this.mainImage, 0, 0, w, h, this.crop);
        } else if (this.baseImage && mode === 'ai') {
          this.drawFitted(this.baseImage, 0, 0, w, h, 'cover');
        } else if (!this.template?.fields?.length) {
          return;
        }
        // Same order as CustomArtworkComposer: artwork → photos → text.
        this.drawTemplateLayers(w, h);
        this.pushToProductCanvas(kind === 'live');
        if (kind !== 'live') this.publishArArt();
      } catch (e) {
        /* never let preview painting block compose */
      }
    }

    drawTemplateLayers(canvasW, canvasH) {
      const fields = Array.isArray(this.template?.fields) ? this.template.fields : [];
      for (const field of fields) {
        if (field.type === 'photo') this.drawPhotoField(field, canvasW, canvasH);
      }
      for (const field of fields) {
        if (field.type === 'text') this.drawTextField(field, canvasW, canvasH);
      }
    }

    fieldBox(field, canvasW, canvasH) {
      const x = Math.round((Number(field.x) / 100) * canvasW);
      const y = Math.round((Number(field.y) / 100) * canvasH);
      const w = Math.max(1, Math.round((Number(field.w) / 100) * canvasW));
      const h = Math.max(1, Math.round((Number(field.h) / 100) * canvasH));
      return { x, y, w, h };
    }

    drawPhotoField(field, canvasW, canvasH) {
      const img = this.fieldImages[field.key];
      if (!img) return;
      const { x, y, w, h } = this.fieldBox(field, canvasW, canvasH);
      const crop = this.fieldCrops?.[field.key];
      if (crop && (crop.scale > 1 || crop.x || crop.y)) {
        this.drawCover(img, x, y, w, h, crop);
        return;
      }
      const src = this.aspectSourceRect(img, field.aspect);
      this.drawFittedSource(img, src, x, y, w, h, this.normalizeFit(field.fit));
    }

    drawTextField(field, canvasW, canvasH) {
      const input = this.querySelector(`[data-custom-field="${CSS.escape(field.key)}"]`);
      const text = (input?.value || '').trim();
      if (!text) return;
      const { x, y, w, h } = this.fieldBox(field, canvasW, canvasH);
      const align = field.align === 'left' || field.align === 'right' ? field.align : 'center';
      const color = this.normalizeColor(field.color);
      const stored = field.font_size == null || field.font_size === '' ? 36 : Number(field.font_size);
      const fontPx = Math.max(10, Math.min(h, Math.round(stored * (canvasW / FONT_REFERENCE_WIDTH))));
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.rect(x, y, w, h);
      this.ctx.clip();
      this.ctx.fillStyle = color;
      this.ctx.font = `${fontPx}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      this.ctx.textAlign = align;
      this.ctx.textBaseline = 'alphabetic';
      const lines = this.wrapText(text, Math.max(8, w - 4));
      const lineHeight = Math.round(fontPx * 1.2);
      const block = lines.length * lineHeight;
      const startY = y + Math.max(0, (h - block) / 2) + fontPx * 0.85;
      const anchorX = align === 'left' ? x + 2 : align === 'right' ? x + w - 2 : x + w / 2;
      lines.forEach((line, i) => {
        this.ctx.fillText(line, anchorX, startY + i * lineHeight);
      });
      this.ctx.restore();
    }

    wrapText(text, maxWidth) {
      const words = String(text).split(/\s+/).filter(Boolean);
      const lines = [];
      let current = '';
      for (const word of words) {
        const tryLine = current === '' ? word : `${current} ${word}`;
        if (this.ctx.measureText(tryLine).width > maxWidth && current !== '') {
          lines.push(current);
          current = word;
        } else {
          current = tryLine;
        }
      }
      if (current) lines.push(current);
      return lines.length ? lines : [text];
    }

    normalizeColor(color) {
      const raw = String(color || '').trim();
      if (/^#?[0-9a-fA-F]{3}$/.test(raw)) {
        const hex = raw.replace('#', '');
        return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
      }
      if (/^#?[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.replace('#', '')}`;
      return '#000000';
    }

    normalizeFit(fit) {
      return fit === 'contain' || fit === 'fill' ? fit : 'cover';
    }

    aspectSourceRect(img, aspect) {
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const m = ASPECT_RE.exec(String(aspect || ''));
      if (!m || !iw || !ih) return { sx: 0, sy: 0, sw: iw, sh: ih };
      const ratio = Number(m[1]) / Math.max(0.0001, Number(m[2]));
      const current = iw / ih;
      if (Math.abs(current - ratio) < 0.01) return { sx: 0, sy: 0, sw: iw, sh: ih };
      if (current > ratio) {
        const sw = ih * ratio;
        return { sx: (iw - sw) / 2, sy: 0, sw, sh: ih };
      }
      const sh = iw / ratio;
      return { sx: 0, sy: (ih - sh) / 2, sw: iw, sh };
    }

    drawFitted(img, destX, destY, destW, destH, fit) {
      this.drawFittedSource(
        img,
        { sx: 0, sy: 0, sw: img.naturalWidth, sh: img.naturalHeight },
        destX,
        destY,
        destW,
        destH,
        this.normalizeFit(fit)
      );
    }

    drawFittedSource(img, src, destX, destY, destW, destH, fit) {
      const sw = src.sw;
      const sh = src.sh;
      if (!sw || !sh || !(destW > 0) || !(destH > 0)) return;
      this.ctx.save();
      this.ctx.beginPath();
      this.ctx.rect(destX, destY, destW, destH);
      this.ctx.clip();
      if (fit === 'fill') {
        this.ctx.drawImage(img, src.sx, src.sy, sw, sh, destX, destY, destW, destH);
        this.ctx.restore();
        return;
      }
      const scale = (fit === 'contain' ? Math.min : Math.max)(destW / sw, destH / sh);
      const dw = sw * scale;
      const dh = sh * scale;
      const x = destX + (destW - dw) / 2;
      const y = destY + (destH - dh) / 2;
      this.ctx.drawImage(img, src.sx, src.sy, sw, sh, x, y, dw, dh);
      this.ctx.restore();
    }

    /**
     * Cover-fill `img` into dest so the locked area never shows empty edges.
     * `crop.scale` >= 1 zooms; `crop.x/y` are percentages (−50…50) of overflow.
     */
    drawCover(img, destX, destY, destW, destH, crop) {
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      if (!iw || !ih || !(destW > 0) || !(destH > 0)) return;
      const scale = this.clampZoom(crop?.scale || 1);
      const cover = Math.max(destW / iw, destH / ih) * scale;
      const dw = iw * cover;
      const dh = ih * cover;
      const extraX = Math.max(0, dw - destW);
      const extraY = Math.max(0, dh - destH);
      const ox = Math.max(-50, Math.min(50, Number(crop?.x) || 0)) / 100;
      const oy = Math.max(-50, Math.min(50, Number(crop?.y) || 0)) / 100;
      const x = destX + (destW - dw) / 2 - ox * extraX;
      const y = destY + (destH - dh) / 2 - oy * extraY;
      const clip = crop?.clip !== false;
      this.ctx.save();
      if (clip) {
        this.ctx.beginPath();
        this.ctx.rect(destX, destY, destW, destH);
        this.ctx.clip();
      }
      this.ctx.drawImage(img, x, y, dw, dh);
      this.ctx.restore();
    }

    printWindow(canvasW, canvasH) {
      const { w, h } = this.parseSize(this.sizeLabel || this.readSizeLabel());
      const aspect = w > 0 && h > 0 ? w / h : 0.7;
      let pw = canvasW * 0.72;
      let ph = pw / aspect;
      const maxH = canvasH * 0.78;
      if (ph > maxH) {
        ph = maxH;
        pw = ph * aspect;
      }
      return { x: (canvasW - pw) / 2, y: (canvasH - ph) / 2, w: pw, h: ph };
    }

    cropDestination() {
      if (this.isCropping()) {
        const rect = this.printWindow(this.canvas.width || 1200, this.canvas.height || 900);
        return { w: rect.w, h: rect.h };
      }
      return { w: this.canvas.width, h: this.canvas.height };
    }

    paintCropScene(canvasW, canvasH) {
      const rect = this.printWindow(canvasW, canvasH);
      this.ctx.fillStyle = '#141414';
      this.ctx.fillRect(0, 0, canvasW, canvasH);
      this.drawCover(this.mainImage, rect.x, rect.y, rect.w, rect.h, { ...this.crop, clip: false });
      this.ctx.save();
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
      this.ctx.beginPath();
      this.ctx.rect(0, 0, canvasW, canvasH);
      this.ctx.rect(rect.x, rect.y, rect.w, rect.h);
      this.ctx.fill('evenodd');
      this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
      this.ctx.globalAlpha = 0.55;
      this.ctx.beginPath();
      this.ctx.moveTo(rect.x + rect.w / 3, rect.y);
      this.ctx.lineTo(rect.x + rect.w / 3, rect.y + rect.h);
      this.ctx.moveTo(rect.x + (rect.w * 2) / 3, rect.y);
      this.ctx.lineTo(rect.x + (rect.w * 2) / 3, rect.y + rect.h);
      this.ctx.moveTo(rect.x, rect.y + rect.h / 3);
      this.ctx.lineTo(rect.x + rect.w, rect.y + rect.h / 3);
      this.ctx.moveTo(rect.x, rect.y + (rect.h * 2) / 3);
      this.ctx.lineTo(rect.x + rect.w, rect.y + (rect.h * 2) / 3);
      this.ctx.stroke();
      this.ctx.restore();
    }

    constrainCrop() {
      this.crop.scale = this.clampZoom(this.crop.scale);
      this.crop.x = Math.max(-50, Math.min(50, Number(this.crop.x) || 0));
      this.crop.y = Math.max(-50, Math.min(50, Number(this.crop.y) || 0));
      if (!this.mainImage || !this.canvas.width) return;
      const dest = this.cropDestination();
      const destW = dest.w;
      const destH = dest.h;
      const cover = Math.max(destW / this.mainImage.naturalWidth, destH / this.mainImage.naturalHeight) * this.crop.scale;
      const extraX = Math.max(0, this.mainImage.naturalWidth * cover - destW);
      const extraY = Math.max(0, this.mainImage.naturalHeight * cover - destH);
      if (extraX <= 0.5) this.crop.x = 0;
      if (extraY <= 0.5) this.crop.y = 0;
    }

    clampZoom(value) {
      return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Number(value) || 1));
    }

    pushToProductCanvas(live) {
      if (this.isCropping()) return;
      this.applyWorkspaceRatio();
      const art = this.getConfiguratorArt();
      if (!this.canvas.width) return;
      if (!art) {
        if (this.hasGalleryPreviewSource()) this.pushToGalleryImage(live);
        return;
      }
      try {
        this.restoreGalleryImage();
      } catch (e) {
        /* never break the PDP */
      }
      const { w, h } = this.parseSize(this.sizeLabel);
      art.dataset.artWidth = String(w);
      art.dataset.artHeight = String(h);
      art.removeAttribute('srcset');
      art.width = this.canvas.width;
      art.height = this.canvas.height;
      const media = this.getStage()?.closest('.fc-canvas');
      if (media) media.style.setProperty('--fc-canvas-ratio', `${w} / ${h}`);
      this.getStage()?.style.setProperty('--fc-canvas-ratio', String(w / h));

      const gen = ++this.paintGen;
      const quality = live ? 0.72 : 0.86;
      const apply = (url) => {
        if (gen !== this.paintGen) {
          if (url && url !== this.artBlobUrl) URL.revokeObjectURL(url);
          return;
        }
        const prev = this.artBlobUrl;
        this.artBlobUrl = url.startsWith('blob:') ? url : null;
        art.src = url;
        if (live) {
          const framed = this.getStage()?.querySelector('.frame-preview__window img');
          if (framed) framed.src = url;
        }
        const finish = () => {
          if (prev && prev !== url) URL.revokeObjectURL(prev);
          if (!live) {
            document.dispatchEvent(new CustomEvent('custom-artwork:changed', { detail: { url } }));
          }
        };
        if (art.complete) finish();
        else art.onload = finish;
      };

      if (typeof this.canvas.toBlob === 'function') {
        this.canvas.toBlob(
          (blob) => {
            if (!blob) {
              try {
                apply(this.canvas.toDataURL('image/jpeg', quality));
              } catch (e) {
                /* tainted canvas — leave the last good preview */
              }
              return;
            }
            apply(URL.createObjectURL(blob));
          },
          'image/jpeg',
          quality
        );
        return;
      }
      try {
        apply(this.canvas.toDataURL('image/jpeg', quality));
      } catch (e) {
        /* tainted */
      }
    }

    /* ---------------- stage pointer / wheel ---------------- */

    /* The theme's product-form listens on the form itself, so the guard sits on
     * document capture: it runs before any form/button listener and
     * stopImmediatePropagation keeps the AJAX add and Buy-it-now from firing. */
    bindAtcGuard() {
      if (this.atcGuardBound) return;
      this.atcGuardBound = true;
      this.boundAtcClick = this.boundAtcClick || this.onAtcClick.bind(this);
      document.addEventListener('submit', this.boundAtcGuard, true);
      document.addEventListener('click', this.boundAtcClick, true);
    }

    unbindAtcGuard() {
      if (!this.atcGuardBound) return;
      this.atcGuardBound = false;
      document.removeEventListener('submit', this.boundAtcGuard, true);
      document.removeEventListener('click', this.boundAtcClick, true);
    }

    isOwnCartForm(form) {
      if (!form || form.tagName !== 'FORM') return false;
      const main = this.cartForm();
      if (form === main) return true;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return false;
      const productId = main?.dataset.productId || this.getProductRoot().dataset?.productId;
      return Boolean(productId) && form.dataset.productId === String(productId);
    }

    atcBlocked() {
      if (this.resetting) return false;
      if (this.hidden && !this.activating) return false;
      if (!this.needsPrintApproval()) return false;
      return !(this.approved && this.token && !this.composeFailed);
    }

    onAtcGuard(e) {
      if (!this.isOwnCartForm(e.target) || !this.atcBlocked()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.explainAtcBlock();
    }

    onAtcClick(e) {
      const hit = e.target?.closest?.('[type="submit"], [name="add"], .shopify-payment-button');
      if (!hit) return;
      if (!this.isOwnCartForm(hit.closest('form')) || !this.atcBlocked()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.explainAtcBlock();
    }

    explainAtcBlock() {
      let message;
      if (!this.imagePath) message = this.dataset.needUploadLabel;
      else if (!this.approved) message = this.dataset.needApproveLabel || this.dataset.needUploadLabel;
      else if (this.composeFailed) message = this.dataset.errorLabel;
      else message = this.dataset.preparingLabel || this.dataset.uploadingLabel;
      this.setStatus(message || '', 'warn');
      try {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (err) {
        /* scrolling is cosmetic */
      }
    }

    bindStage() {
      const stage = this.getStage();
      if (!stage) return;
      if (this.boundStage === stage && stage.dataset.customBound === 'true') return;
      this.unbindStage();
      this.boundStage = stage;
      stage.dataset.customBound = 'true';
      stage.addEventListener('pointerdown', this.boundPointerDown);
      stage.addEventListener('wheel', this.boundWheel, { passive: false });
      window.addEventListener('pointermove', this.boundPointerMove);
      window.addEventListener('pointerup', this.boundPointerUp);
      window.addEventListener('pointercancel', this.boundPointerUp);
    }

    unbindStage() {
      const stage = this.boundStage || this.getStage();
      if (stage) {
        stage.removeEventListener('pointerdown', this.boundPointerDown);
        stage.removeEventListener('wheel', this.boundWheel);
        delete stage.dataset.customBound;
        stage.classList.remove('is-custom-editing', 'is-panning');
      }
      this.boundStage = null;
      window.removeEventListener('pointermove', this.boundPointerMove);
      window.removeEventListener('pointerup', this.boundPointerUp);
      window.removeEventListener('pointercancel', this.boundPointerUp);
    }

    onPointerDown(e) {
      if (!this.canCrop() || e.button === 2) return;
      this.dragging = true;
      this.lastPointer = { x: e.clientX, y: e.clientY };
      this.getStage()?.classList.add('is-panning');
      try {
        e.currentTarget.setPointerCapture?.(e.pointerId);
      } catch (err) {
        /* capture is optional */
      }
    }

    onPointerMove(e) {
      if (!this.dragging || !this.lastPointer || !this.canCrop()) return;
      const art = this.getArt();
      const rect = (art || this.getStage())?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return;
      const dx = e.clientX - this.lastPointer.x;
      const dy = e.clientY - this.lastPointer.y;
      this.lastPointer = { x: e.clientX, y: e.clientY };

      const dest = this.cropDestination();
      const scaleX = this.canvas.width / rect.width;
      const scaleY = this.canvas.height / rect.height;
      const cover = Math.max(dest.w / this.mainImage.naturalWidth, dest.h / this.mainImage.naturalHeight) * this.crop.scale;
      const extraX = Math.max(0, this.mainImage.naturalWidth * cover - dest.w);
      const extraY = Math.max(0, this.mainImage.naturalHeight * cover - dest.h);
      if (extraX > 0.5) this.crop.x -= ((dx * scaleX) / extraX) * 100;
      if (extraY > 0.5) this.crop.y -= ((dy * scaleY) / extraY) * 100;
      this.constrainCrop();
      this.schedulePaint('live');
    }

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;
      this.lastPointer = null;
      this.getStage()?.classList.remove('is-panning');
      this.schedulePaint('settle');
      this.kickCompose();
    }

    onWheel(e) {
      if (!this.canCrop()) return;
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
      this.crop.scale = this.clampZoom(this.crop.scale * factor);
      if (this.zoom) this.zoom.value = String(this.crop.scale);
      this.constrainCrop();
      this.schedulePaint('live');
      this.kickCompose();
    }

    /* ---------------- compose / line properties ---------------- */

    kickCompose(immediate) {
      if (this.needsPrintApproval() && !this.approved) return;
      clearTimeout(this.composeTimer);
      if (immediate) {
        this.compose();
        return;
      }
      this.composeTimer = setTimeout(() => this.compose(), COMPOSE_MS);
    }

    async compose() {
      if (!this.dash || this.resetting) return;
      const rawSize = this.sizeLabel || this.readSizeLabel() || '50x70';
      const oriented = this.parseSize(rawSize);
      const fmtCm = (n) => String(Math.round(n * 10) / 10);
      const size = this.orientation === 'landscape' ? `${fmtCm(oriented.w)}x${fmtCm(oriented.h)}` : rawSize;
      const mode = this.mode();
      const hasImage = Boolean(this.imagePath) || (mode === 'template' && Boolean(this.template?.id));
      if (!hasImage) return;
      const seq = ++this.composeSeq;
      try {
        const body = {
          template_id: this.template?.id || null,
          mode,
          size,
          image_path: this.imagePath || null,
          crop: { scale: this.crop.scale, x: this.crop.x, y: this.crop.y },
          payload: this.fieldValues(),
          frame: (this.frameChoice || '').slice(0, 80) || null,
          passepartout: (this.matChoice || '').slice(0, 80) || null,
        };
        const res = await fetch(`${this.dash}/api/custom/compose`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (seq !== this.composeSeq) return;
        if (!res.ok) {
          this.composeFailed = true;
          this.setStatus(json.message || this.dataset.errorLabel || '', true);
          return;
        }
        this.composeFailed = false;
        this.token = json.token;
        this.writeLineProperties(json);
        // Keep the local canvas as the live preview — do not swap in the
        // server JPEG (that would hitch and fight in-progress pan/zoom).
        if (json.pdf_name && !this.warnIfLowRes()) this.setStatus(json.pdf_name);
      } catch (e) {
        if (seq === this.composeSeq) {
          this.composeFailed = true;
          this.setStatus(this.dataset.errorLabel || '', true);
        }
      }
    }

    fieldValues() {
      const out = {};
      const fields = Array.isArray(this.template?.fields) ? this.template.fields : [];
      this.querySelectorAll('[data-custom-field]').forEach((el) => {
        out[el.getAttribute('data-custom-field')] = el.value;
      });
      this.querySelectorAll('[data-custom-photo]').forEach((el) => {
        const key = el.getAttribute('data-custom-photo');
        const path = this.fieldPaths[key] || el.dataset.path;
        if (!path) return;
        const field = fields.find((f) => f.key === key);
        const fit = this.normalizeFit(field?.fit);
        const crop = this.fieldCrops[key];
        const payload = { path, fit };
        if (crop && (crop.scale > 1 || crop.x || crop.y)) payload.crop = crop;
        out[key] = payload;
      });
      return out;
    }

    writeLineProperties(json) {
      const form = this.cartForm();
      if (!form) return;
      const ensure = (name, value) => {
        let input = form.querySelector(`input[name="properties[${name}]"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          form.appendChild(input);
        }
        input.value = value || '';
      };
      if (json.token) ensure('_custom_token', json.token);
      if (json.pdf_name) ensure('_print_pdf', json.pdf_name);
      if (json.preview_url) {
        ensure('_custom_preview', json.preview_url);
        ensure('Baskı Dosyası', json.preview_url);
      }
      if (json.original_url) ensure('_Orijinal Dosya', json.original_url);
      ensure('_poster_size', json.size || this.sizeLabel || '');
      ensure('_Yön', this.orientation === 'landscape' ? 'Yatay' : 'Dikey');
      ensure('_crop_json', JSON.stringify({ scale: this.crop.scale, x: this.crop.x, y: this.crop.y }));
      if (json.dpi != null) ensure('_print_dpi', String(json.dpi));
      this.writeConfigProperties();
      try {
        // Panelin verdiği tam adresler (siparişte ve depoda açılır); yoksa eski düzen.
        if (json.pdf_view_url) ensure('_pdf_goruntule', json.pdf_view_url);
        else if (json.token && this.dash) ensure('_pdf_goruntule', `${this.dash}/custom-prints/${json.token}?view=1`);
        if (json.pdf_download_url) ensure('_pdf_indir', json.pdf_download_url);
        else if (json.token && this.dash) ensure('_pdf_indir', `${this.dash}/custom-prints/${json.token}`);
      } catch (e) {}
    }

    writeConfigProperties() {
      if (this.hidden || !this.token) return;
      const form = this.cartForm();
      if (!form) return;
      const set = (name, value) => {
        let input = form.querySelector(`input[name="properties[${name}]"]`);
        if (!value) {
          input?.remove();
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          form.appendChild(input);
        }
        input.value = value;
      };
      set('_Paspartu', this.matChoice || '');
      set('_Çerçeve', this.frameChoice || '');
    }

    clearLineProperties() {
      const form = this.cartForm();
      if (!form) return;
      form.querySelectorAll('input[name="properties[_Paspartu]"], input[name="properties[_Çerçeve]"]').forEach((el) => el.remove());
      form.querySelectorAll('input[name="properties[_Yön]"], input[name="properties[_custom_token]"], input[name="properties[_print_pdf]"], input[name="properties[_custom_preview]"], input[name="properties[_pdf_goruntule]"], input[name="properties[_pdf_indir]"], input[name="properties[Baskı Dosyası]"], input[name="properties[_Orijinal Dosya]"], input[name="properties[_poster_size]"], input[name="properties[_crop_json]"], input[name="properties[_print_dpi]"]').forEach((el) => {
        el.value = '';
      });
    }

    /* ---------------- reset ---------------- */

    onCartUpdated(event) {
      // cart.js quantity edits include detail.cart — do not wipe a new design.
      if (event?.detail?.cart) return;
      this.resetAfterAdd();
    }

    resetAfterAdd() {
      if (this.resetting) return;
      this.resetting = true;
      this.reset({ restoreArt: true });
      this.resetting = false;
    }

    reset({ restoreArt } = {}) {
      clearTimeout(this.composeTimer);
      this.paintGen += 1;
      this.composeSeq += 1;
      this.imagePath = null;
      this.token = null;
      this.composeFailed = false;
      this.approved = false;
      this.mainFile = null;
      this.rotation = 0;
      this.rotPaths = {};
      this.orientation = 'portrait';
      this.syncOrientUi();
      this.crop = { scale: 1, x: 0, y: 0 };
      this.mainImage = null;
      this.fieldImages = {};
      this.fieldPaths = {};
      this.fieldCrops = {};
      if (this.fileInput) this.fileInput.value = '';
      if (this.zoom) this.zoom.value = '1';
      this.querySelectorAll('[data-custom-field]').forEach((el) => {
        el.value = '';
      });
      this.querySelectorAll('[data-custom-photo]').forEach((el) => {
        el.value = '';
        delete el.dataset.path;
      });
      this.querySelectorAll('.custom-editor__thumb').forEach((el) => el.remove());
      this.clearLineProperties();
      this.setStatus('');
      this.setBusy(false);
      this.disconnectGalleryObserver();
      this.restoreGalleryImage();
      this.revokeAll();
      // Yaris: sıfırlayınca kırpma görünümü de kapansın (Onayla butonu ve boş çalışma alanı kalmasın).
      this.syncCropChrome();
      if (restoreArt) this.restoreOriginalArt();
      else if (this.baseImage && this.mode() === 'template') this.schedulePaint('settle');
      else this.schedulePaint('settle');
    }

    snapshotOriginalArt() {
      const art = this.getConfiguratorArt();
      if (!art || this.originalArt) return;
      this.originalArt = {
        src: art.getAttribute('src') || '',
        srcset: art.getAttribute('srcset') || '',
        sizes: art.getAttribute('sizes') || '',
        width: art.getAttribute('width') || '',
        height: art.getAttribute('height') || '',
        artWidth: art.dataset.artWidth || '',
        artHeight: art.dataset.artHeight || '',
        ratio: this.getStage()?.style.getPropertyValue('--fc-canvas-ratio') || '',
        mediaRatio: art.closest('.fc-canvas')?.style.getPropertyValue('--fc-canvas-ratio') || '',
      };
    }

    restoreOriginalArt() {
      const art = this.getConfiguratorArt();
      const snap = this.originalArt;
      if (!art || !snap) {
        this.schedulePaint('settle');
        return;
      }
      if (snap.srcset) art.setAttribute('srcset', snap.srcset);
      else art.removeAttribute('srcset');
      if (snap.sizes) art.setAttribute('sizes', snap.sizes);
      if (snap.width) art.setAttribute('width', snap.width);
      if (snap.height) art.setAttribute('height', snap.height);
      if (snap.artWidth) art.dataset.artWidth = snap.artWidth;
      else delete art.dataset.artWidth;
      if (snap.artHeight) art.dataset.artHeight = snap.artHeight;
      else delete art.dataset.artHeight;
      art.src = snap.src;
      const stage = this.getStage();
      const media = stage?.closest('.fc-canvas');
      if (stage && snap.ratio) stage.style.setProperty('--fc-canvas-ratio', snap.ratio);
      if (media && snap.mediaRatio) media.style.setProperty('--fc-canvas-ratio', snap.mediaRatio);
      const fire = () => document.dispatchEvent(new CustomEvent('custom-artwork:changed', { detail: { url: snap.src } }));
      if (art.complete) fire();
      else art.onload = fire;
    }

    hasGalleryPreviewSource() {
      return Boolean(this.mainImage || (this.mode() === 'template' && this.baseImage));
    }

    getGalleryImage() {
      try {
        const root = this.getProductRoot();
        const wrapper =
          root.querySelector('.product__media-wrapper') ||
          document.querySelector('.product__media-wrapper');
        const scope = wrapper || root;
        const active =
          scope.querySelector('.product__media-item.is-active') ||
          scope.querySelector('.product__media-item[aria-hidden="false"]') ||
          scope.querySelector('.product__media-item:not([hidden])');
        const img =
          (active && active.querySelector('.product__media img')) ||
          scope.querySelector('.product__media img') ||
          document.querySelector('.product__media img');
        if (!img || !img.isConnected) return null;
        if (this.contains(img)) return null;
        return img;
      } catch (e) {
        return null;
      }
    }

    snapshotGalleryImage(img) {
      if (!img) return;
      if (this.galleryImg === img && this.originalGallery) return;
      if (this.galleryImg && this.galleryImg !== img) {
        const prev = this.galleryImg;
        const snap = this.originalGallery;
        this.disconnectGalleryObserver();
        if (prev.isConnected && snap) {
          this.galleryApplying = true;
          try {
            this.applyGallerySnapshot(prev, snap);
          } catch (e) {
            /* never break the PDP */
          }
          this.galleryApplying = false;
        }
        this.originalGallery = null;
        this.galleryImg = null;
      }
      if (this.originalGallery && this.galleryImg === img) return;
      this.originalGallery = {
        src: img.getAttribute('src') || '',
        srcset: img.getAttribute('srcset') || '',
        sizes: img.getAttribute('sizes') || '',
        style: img.getAttribute('style') || '',
      };
      this.galleryImg = img;
      this.observeGalleryImage(img);
    }

    applyGallerySnapshot(img, snap) {
      if (!img || !snap) return;
      if (snap.srcset) img.setAttribute('srcset', snap.srcset);
      else img.removeAttribute('srcset');
      if (snap.sizes) img.setAttribute('sizes', snap.sizes);
      else img.removeAttribute('sizes');
      if (snap.src) img.setAttribute('src', snap.src);
      else img.removeAttribute('src');
      if (snap.style) img.setAttribute('style', snap.style);
      else img.removeAttribute('style');
    }

    writeGalleryImage(img, url) {
      if (!img || !url) return;
      this.galleryApplying = true;
      try {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.style.objectFit = 'contain';
        if (img.src !== url) img.src = url;
      } catch (e) {
        /* never break the PDP */
      }
      this.galleryApplying = false;
    }

    observeGalleryImage(img) {
      this.disconnectGalleryObserver();
      if (!img || typeof MutationObserver !== 'function') return;
      this.galleryObserver = new MutationObserver(() => {
        if (this.galleryApplying || this.resetting || !this.galleryPreviewUrl) return;
        if (this.getConfiguratorArt()) return;
        try {
          const current = this.getGalleryImage() || img;
          if (!current.isConnected) return;
          if (current !== img) {
            this.snapshotGalleryImage(current);
          }
          if (current.src === this.galleryPreviewUrl && !current.getAttribute('srcset')) return;
          this.writeGalleryImage(current, this.galleryPreviewUrl);
        } catch (e) {
          /* never break the PDP */
        }
      });
      this.galleryObserver.observe(img, { attributes: true, attributeFilter: ['src', 'srcset', 'sizes'] });
    }

    disconnectGalleryObserver() {
      if (!this.galleryObserver) return;
      this.galleryObserver.disconnect();
      this.galleryObserver = null;
    }

    restoreGalleryImage() {
      const img = this.galleryImg;
      const snap = this.originalGallery;
      this.disconnectGalleryObserver();
      this.galleryPreviewUrl = null;
      this.galleryImg = null;
      this.originalGallery = null;
      if (!img || !snap) return;
      this.galleryApplying = true;
      try {
        this.applyGallerySnapshot(img, snap);
      } catch (e) {
        /* never break the PDP */
      }
      this.galleryApplying = false;
    }

    reapplyGalleryPreview() {
      try {
        if (this.resetting || !this.galleryPreviewUrl || !this.canvas.width) return;
        if (this.getConfiguratorArt()) return;
        const img = this.getGalleryImage();
        if (!img) return;
        this.snapshotGalleryImage(img);
        this.writeGalleryImage(img, this.galleryPreviewUrl);
      } catch (e) {
        /* never break the PDP */
      }
    }

    pushToGalleryImage(live) {
      try {
        const img = this.getGalleryImage();
        if (!img) return;
        this.snapshotGalleryImage(img);
        const gen = ++this.paintGen;
        const quality = live ? 0.72 : 0.86;
        const apply = (url) => {
          if (gen !== this.paintGen) {
            if (url && url !== this.artBlobUrl) URL.revokeObjectURL(url);
            return;
          }
          const prev = this.artBlobUrl;
          this.artBlobUrl = url.startsWith('blob:') ? url : null;
          this.galleryPreviewUrl = url;
          const target = this.getGalleryImage() || img;
          if (target !== img) this.snapshotGalleryImage(target);
          this.writeGalleryImage(target, url);
          const finish = () => {
            if (prev && prev !== url) URL.revokeObjectURL(prev);
            if (!live) {
              document.dispatchEvent(new CustomEvent('custom-artwork:changed', { detail: { url } }));
            }
          };
          if (target.complete) finish();
          else target.onload = finish;
        };

        if (typeof this.canvas.toBlob === 'function') {
          this.canvas.toBlob(
            (blob) => {
              if (!blob) {
                try {
                  apply(this.canvas.toDataURL('image/jpeg', quality));
                } catch (e) {
                  /* tainted canvas — leave the last good preview */
                }
                return;
              }
              apply(URL.createObjectURL(blob));
            },
            'image/jpeg',
            quality
          );
          return;
        }
        apply(this.canvas.toDataURL('image/jpeg', quality));
      } catch (e) {
        /* never break the PDP */
      }
    }

    /* ---------------- helpers ---------------- */

    getProductRoot() {
      return this.closest('product-info') || document;
    }

    getConfiguratorStage() {
      return this.getProductRoot().querySelector('[data-fc-canvas] [data-fc-canvas-stage]');
    }

    getConfiguratorArt() {
      return this.getConfiguratorStage()?.querySelector('[data-fc-canvas-art]');
    }

    hasConfigurator() {
      return Boolean(this.getConfiguratorStage());
    }

    ensureWorkspace() {
      this.ownWorkspace = this.ownWorkspace || this.querySelector('[data-custom-workspace]');
      this.ownStage = this.ownStage || this.querySelector('[data-custom-stage]');
      if (this.isCropping()) {
        this.classList.add('custom-editor--own-workspace');
        if (this.ownWorkspace) this.ownWorkspace.hidden = false;
        if (this.ownStage && this.canvas.parentNode !== this.ownStage) {
          this.canvas.setAttribute('data-custom-art', '');
          this.ownStage.appendChild(this.canvas);
        }
        return;
      }
      if (this.hasConfigurator()) {
        this.classList.remove('custom-editor--own-workspace');
        if (this.ownWorkspace) this.ownWorkspace.hidden = true;
        if (this.canvas.hasAttribute('data-custom-art')) this.canvas.removeAttribute('data-custom-art');
        if (this.ownStage && this.canvas.parentNode === this.ownStage) this.ownStage.removeChild(this.canvas);
        return;
      }
      this.classList.add('custom-editor--own-workspace');
      if (this.ownWorkspace) this.ownWorkspace.hidden = false;
      if (this.ownStage && this.canvas.parentNode !== this.ownStage) {
        this.canvas.setAttribute('data-custom-art', '');
        this.ownStage.appendChild(this.canvas);
      }
    }

    getStage() {
      if (this.isCropping() && this.ownStage) return this.ownStage;
      return this.getConfiguratorStage() || this.ownStage || this.querySelector('[data-custom-stage]');
    }

    getArt() {
      if (this.isCropping()) return this.canvas;
      return this.getConfiguratorArt() || this.querySelector('[data-custom-art]') || this.canvas;
    }

    cartForm() {
      return this.getProductRoot().querySelector('form[data-type="add-to-cart-form"]') || document.querySelector('form[data-type="add-to-cart-form"]');
    }

    loadImage(url, cors) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        if (cors) img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image'));
        img.src = url;
      });
    }

    rememberUrl(url) {
      this.objectUrls.push(url);
      return url;
    }

    revokeAll() {
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.objectUrls = [];
      if (this.artBlobUrl) {
        URL.revokeObjectURL(this.artBlobUrl);
        this.artBlobUrl = null;
      }
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', Boolean(busy));
      [this.pick, this.aiBtn, this.resetBtn, this.drop, this.rotateBtn, ...(this.orientBtns || [])].forEach((btn) => {
        if (btn) btn.disabled = Boolean(busy);
      });
    }

    setStatus(text, kind) {
      if (!this.status) return;
      // Yaris: "-" boş mesaj demek (ör. düşük çözünürlük uyarısı kapalı) — tek başına tire gösterme.
      if (typeof text === 'string' && text.trim() === '-') text = '';
      const isError = kind === true || kind === 'error';
      const isWarn = kind === 'warn';
      this.status.hidden = !text;
      this.status.textContent = text || '';
      this.status.classList.toggle('is-error', Boolean(isError && text));
      this.status.classList.toggle('is-warn', Boolean(isWarn && text));
    }
  }

  customElements.define('custom-product-editor', CustomProductEditor);
}
